/**
 * useSupplierFeeHandlers
 * 협력사 수수료 관리 핸들러 훅
 */
import { useState, useCallback } from 'react';
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import { useUserInfo } from '@/shared/hooks/useCrudOperations';
import { riderFeeApi, SaveRiderFeeResponse } from '../api/riderFeeApi';
import {
    RiderCommissionRow,
    CommissionHistoryItem,
    validateFeeData
} from '../utils/supplierFeeHelpers';

/** 수수료 적용 예정일 정보 */
export interface FeeApplyDateInfo {
    applyStartDate: string;
    isImmediateApply: boolean;
}

interface UseSupplierFeeHandlersProps {
    selectedSupplierId?: string;
    commissionData: RiderCommissionRow[];
    setCommissionData: React.Dispatch<React.SetStateAction<RiderCommissionRow[]>>;
    fetchRiderFees: () => Promise<void>;
}

export const useSupplierFeeHandlers = ({
    selectedSupplierId,
    commissionData,
    setCommissionData,
    fetchRiderFees
}: UseSupplierFeeHandlersProps) => {
    const { showToast } = useToast();
    const { getUserInfo } = useUserInfo();

    // 히스토리 모달 상태
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [selectedRider, setSelectedRider] = useState('');
    const [historyData, setHistoryData] = useState<CommissionHistoryItem[]>([]);

    // 수수료 적용 예정일 모달 상태
    const [showFeeApplyModal, setShowFeeApplyModal] = useState(false);
    const [feeApplyDateInfo, setFeeApplyDateInfo] = useState<FeeApplyDateInfo | null>(null);

    /**
     * 수수료 구분 변경 (금액 초기화 포함)
     */
    const handleCommissionTypeChange = useCallback((riderId: string, value: string) => {
        setCommissionData(prev => prev.map(row => {
            if (row.riderId === riderId) {
                return {
                    ...row,
                    commissionType: value,
                    amount: '',
                    isEdited: true
                };
            }
            return row;
        }));
    }, [setCommissionData]);

    /**
     * 금액 변경
     */
    const handleAmountChange = useCallback((riderId: string, value: string) => {
        setCommissionData(prev => prev.map(row => {
            if (row.riderId === riderId) {
                return {
                    ...row,
                    amount: value,
                    isEdited: true
                };
            }
            return row;
        }));
    }, [setCommissionData]);

    /**
     * 저장
     */
    const handleSave = useCallback(async (riderId: string) => {
        const row = commissionData.find(r => r.riderId === riderId);
        if (!row) return;

        // 유효성 검사
        const validation = validateFeeData(row.commissionType, row.amount);
        if (!validation.valid) {
            showToast(validation.message, { type: ToastType.WARNING });
            return;
        }

        const userInfo = getUserInfo();
        if (!userInfo) return;

        const targetSupplierId = selectedSupplierId || userInfo.suppId;
        if (!targetSupplierId) {
            showToast('협력사 정보를 찾을 수 없습니다.', { type: ToastType.ERROR });
            return;
        }

        try {
            const response: SaveRiderFeeResponse = await riderFeeApi.saveRiderFee({
                suppId: targetSupplierId,
                riderId: row.riderId,
                commissionType: row.commissionType,
                amount: parseInt(row.amount.replace(/,/g, '')),
                applyTiming: 'IMMEDIATE',
                userId: userInfo.userId
            });

            showToast('수수료가 저장되었습니다.', { type: ToastType.SUCCESS });

            // 저장 후 최신 데이터 새로고침
            await fetchRiderFees();

            // 적용 예정일 모달 표시
            setFeeApplyDateInfo({
                applyStartDate: response.applyStartDate,
                isImmediateApply: response.isImmediateApply
            });
            setShowFeeApplyModal(true);
        } catch {
            showToast('수수료 저장에 실패했습니다.', { type: ToastType.ERROR });
        }
    }, [commissionData, selectedSupplierId, getUserInfo, fetchRiderFees, showToast]);

    /**
     * 히스토리 조회
     */
    const handleShowHistory = useCallback(async (riderId: string, riderName: string) => {
        const userInfo = getUserInfo();
        if (!userInfo) {
            showToast('사용자 정보를 찾을 수 없습니다.', { type: ToastType.ERROR });
            return;
        }

        const targetSupplierId = selectedSupplierId || userInfo.suppId;
        if (!targetSupplierId) {
            showToast('협력사 정보를 찾을 수 없습니다.', { type: ToastType.ERROR });
            return;
        }

        try {
            const history = await riderFeeApi.getRiderFeeHistory(targetSupplierId, riderId);
            setHistoryData(history);
            setSelectedRider(riderName);
            setShowHistoryModal(true);
        } catch {
            showToast('히스토리를 불러오는데 실패했습니다.', { type: ToastType.ERROR });
        }
    }, [selectedSupplierId, getUserInfo, showToast]);

    /**
     * 히스토리 모달 닫기
     */
    const handleCloseHistoryModal = useCallback(() => {
        setShowHistoryModal(false);
    }, []);

    /**
     * 수수료 적용 예정일 모달 닫기
     */
    const handleCloseFeeApplyModal = useCallback(() => {
        setShowFeeApplyModal(false);
        setFeeApplyDateInfo(null);
    }, []);

    return {
        showHistoryModal,
        selectedRider,
        historyData,
        showFeeApplyModal,
        feeApplyDateInfo,
        handleCommissionTypeChange,
        handleAmountChange,
        handleSave,
        handleShowHistory,
        handleCloseHistoryModal,
        handleCloseFeeApplyModal
    };
};
